import { onCompletePaymentSurvey } from "@pageDefinitions/actions/survey/onCompletePaymentSurvey";
import { onCompleteEmployerSurvey } from "@pageDefinitions/actions/survey/onCompleteEmployerSurvey";
import { onStartMainSurvey } from "@pageDefinitions/actions/survey/onStartMainSurvey";
import { onCompleteMainSurvey } from "@pageDefinitions/actions/survey/onCompleteMainSurvey";
import { onCompleteLandingPageSurvey } from "@pageDefinitions/actions/survey/onCompleteLandingPageSurvey";
import {
  onCompleteCancellationSurvey,
  onStartCancellationSurvey,
} from "src/pageDefinitions/actions/survey/cancellationSurveysActions";
import { onHRTLeadCaptured } from "@pageDefinitions/actions/survey/onHRTLeadCaptured";
import {
  mixpanelTrackAction,
  noomEventTrackAction,
} from "@pageDefinitions/actions/tracking";

import { GoToPageOptions } from "../goto/pageSet";
import { PageListEntry, PageSet } from "../types";
import { onStartSurvey } from "./survey/onStartSurvey";
import {
  saveAnswers,
  saveNoomPremiumAnswers,
  saveCoachingSurveyAnswers,
  savePremiumWorkoutAnswers,
  savePremiumMealPlanAnswers,
  saveOnboardingSurveyAnswers,
} from "./survey/saveAnswers";
import { SaveEventToBraze } from "./saveToBraze";
import { saveToSession } from "./session";
import { onCompleteTelehealthFeedbackSurvey } from "@pageDefinitions/actions/survey/onCompleteTelehealthFeedbackSurvey";
import { onStartTelehealthFeedbackSurvey } from "@pageDefinitions/actions/survey/onStartTelehealthFeedbackSurvey";
import { trackVibeOfferSuppressed } from "./survey/trackVibeOfferSuppressed";
import { trackVibeOfferViewed } from "./survey/trackVibeOfferViewed";
import { setZumbaWhereDidYouHear } from "./survey/setZumbaWhereDidYouHear";
import { setCalAIWhereDidYouHear } from "./survey/setCalAIWhereDidYouHear";
import { trackCurrentTierEvent } from "@utils/counterOffer";
import { saveRecommendedPlanToCRM } from "./survey/saveRecommendedPlanToCRM";
import { updatePlanFromMedRedirect } from "@utils/redux/slices/recommendedPlan";
import { onStartCourseSurvey } from "./survey/onStartCourseSurvey";
import { onCompleteCourseSurvey } from "./survey/onCompleteCourseSurvey";
import { onCompleteSurvey } from "@pageDefinitions/actions/survey/onCompleteSurvey";
import {
  seedCurrentHealthRiskMedicalFromDiabetesType,
  seedCurrentHealthRiskMedicalFromMetabolicDiagnoses,
} from "./survey/seedCurrentHealthRiskMedical";
import { onCompletePDQuestions } from "./survey/onCompletePDQuestions";
import { setTelehealthAuthentication } from "./survey/setTelehealthAuthentication";
import {
  loadEligibleSaleItems,
  loadRecommendedMedPlans,
  waitForRecommendedMedPlans,
} from "./survey/setRecommendedMedPlans";
import { loadRecommendedHealthPlan } from "./survey/loadRecommendedHealthPlan";
import { persistUserDataFeatureRolloutType } from "./survey/persistUserDataFeatureRolloutType";
import { setGlobalExitInUpsell } from "./survey/setGlobalExitInUpsell";
import {
  loadFacialBloodScanIntroMetadata,
  waitForFacialBloodScanIntroMetadata,
} from "./survey/loadFacialBloodScanIntroMetadata";
import { fetchAndUpdateB2BUserDetails } from "./survey/fetchAndUpdateB2BUserDetails";
import {
  loadAssessmentPrivacyPolicy,
  waitForAssessmentPrivacyPolicy,
} from "./survey/loadAssessmentPrivacyPolicy";
import { onStartMedDowngradeSurvey } from "@pageDefinitions/actions/survey/medDowngradeSurveysActions";
import { onCompleteHealthSurvey } from "./survey/onCompleteHealthSurvey";
import { autoSetMostImportantGoal } from "./survey/autoSetMostImportantGoal";
import { setIdealWeightToCurrentWeight } from "./survey/setIdealWeightToCurrentWeight";

export type ActionHandlerParams = {
  params: JsonObject;
  pageSet: PageSet;
  page: PageListEntry;
} & GoToPageOptions;

export type ActionHandler = (
  params: ActionHandlerParams
) => void | Promise<void>;

const ACTIONS = {
  TRACK: mixpanelTrackAction,
  TRACK_BUYFLOW: noomEventTrackAction,
  SAVE_EVENT_TO_BRAZE: SaveEventToBraze,

  SAVE_TO_SESSION: saveToSession,

  SAVE_ANSWERS: saveAnswers,
  SAVE_NOOM_PREMIUM_ANSWERS: saveNoomPremiumAnswers,
  SAVE_PREMIUM_MEAL_PLAN_ANSWERS: savePremiumMealPlanAnswers,
  SAVE_PREMIUM_WORKOUT_PLAN_ANSWERS: savePremiumWorkoutAnswers,
  SAVE_COACHING_SURVEY_ANSWERS: saveCoachingSurveyAnswers,

  SAVE_RECOMMENDED_PLAN_TO_CRM: saveRecommendedPlanToCRM,

  // Generic Survey
  ON_START_SURVEY: onStartSurvey,
  ON_COMPLETE_SURVEY: onCompleteSurvey,

  // Main Survey
  ON_START_MAIN_SURVEY: onStartMainSurvey,
  ON_COMPLETE_MAIN_SURVEY: onCompleteMainSurvey,

  // Cancellation Survey
  ON_START_CANCELLATION_SURVEY: onStartCancellationSurvey,
  ON_COMPLETE_CANCELLATION_SURVEY: onCompleteCancellationSurvey,

  // Med Downgrade Survey
  ON_START_MED_DOWNGRADE_SURVEY: onStartMedDowngradeSurvey,

  // Payment Survey
  ON_COMPLETE_PAYMENT_SURVEY: onCompletePaymentSurvey,

  // Employer Survey
  ON_COMPLETE_EMPLOYER_SURVEY: onCompleteEmployerSurvey,

  // Telehealth Feedback Survey
  ON_START_TELEHEALTH_FEEDBACK_SURVEY: onStartTelehealthFeedbackSurvey,
  ON_COMPLETE_TELEHEALTH_FEEDBACK_SURVEY: onCompleteTelehealthFeedbackSurvey,

  // Landing page Survey
  ON_COMPLETE_LANDING_PAGE_SURVEY: onCompleteLandingPageSurvey,

  SET_ZUMBA_WHERE_DID_YOU_HEAR: setZumbaWhereDidYouHear,
  SET_CAL_AI_WHERE_DID_YOU_HEAR: setCalAIWhereDidYouHear,

  TRACK_VIBE_OFFER_VIEWED: trackVibeOfferViewed,
  TRACK_VIBE_OFFER_SUPPRESSED: trackVibeOfferSuppressed,

  TRACK_CURRENT_TIER_EVENT: trackCurrentTierEvent,

  UPDATE_PLAN_FROM_MED_REDIRECT: updatePlanFromMedRedirect,

  ON_START_COURSE_SURVEY: onStartCourseSurvey,
  ON_COMPLETE_COURSE_SURVEY: onCompleteCourseSurvey,
  ON_HRT_LEAD_CAPTURED: onHRTLeadCaptured,

  SEED_CURRENT_HEALTH_RISK_MEDICAL_FROM_DIABETES_TYPE:
    seedCurrentHealthRiskMedicalFromDiabetesType,
  SEED_CURRENT_HEALTH_RISK_MEDICAL_FROM_METABOLIC_DIAGNOSES:
    seedCurrentHealthRiskMedicalFromMetabolicDiagnoses,

  ON_COMPLETE_PD_QUESTIONS: onCompletePDQuestions,

  SET_TELEHEALTH_AUTHENTICATION: setTelehealthAuthentication,

  LOAD_RECOMMENDED_MED_PLANS: loadRecommendedMedPlans,
  WAIT_FOR_RECOMMENDED_MED_PLANS: waitForRecommendedMedPlans,
  LOAD_ELIGIBLE_SALE_ITEMS: loadEligibleSaleItems,

  LOAD_RECOMMENDED_HEALTH_PLAN: loadRecommendedHealthPlan,

  PERSIST_USERDATA_FEATURE_ROLLOUT_TYPE: persistUserDataFeatureRolloutType,

  SET_GLOBAL_EXIT_IN_UPSELL: setGlobalExitInUpsell,

  // Facial Blood Scan
  LOAD_FACIAL_BLOOD_SCAN_INTRO_METADATA: loadFacialBloodScanIntroMetadata,
  WAIT_FOR_FACIAL_BLOOD_SCAN_INTRO_METADATA:
    waitForFacialBloodScanIntroMetadata,

  // Get user details for B2B users entering med upsell flow
  FETCH_AND_UPDATE_B2B_USER_DETAILS: fetchAndUpdateB2BUserDetails,

  LOAD_ASSESSMENT_PRIVACY_POLICY: loadAssessmentPrivacyPolicy,
  WAIT_FOR_ASSESSMENT_PRIVACY_POLICY: waitForAssessmentPrivacyPolicy,

  SAVE_ONBOARDING_SURVEY_ANSWERS: saveOnboardingSurveyAnswers,

  // Health Survey
  ON_COMPLETE_HEALTH_SURVEY: onCompleteHealthSurvey,
  AUTO_SET_MOST_IMPORTANT_GOAL: autoSetMostImportantGoal,
  SET_IDEAL_WEIGHT_TO_CURRENT_WEIGHT: setIdealWeightToCurrentWeight,
};

export default ACTIONS;
